import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  TextField,
  Autocomplete,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Save as SaveIcon,
  Launch as LaunchIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Strategy } from '../../types/strategy';
import { StrategyStatus, StrategyType, StrategyMode } from '../../types/strategy';
import { useAuth } from '../../contexts/AuthContext';
import { firestoreService } from '../../services/firestore';
// Old SimpleStrategyBuilder removed
import VisualStrategyBuilder from '../../components/strategy/VisualStrategyBuilder';
import { PyStrategyEditor } from '../../components/strategy/PyStrategyEditor';
import { pineScriptService, PineScriptVersion } from '../../services/pineScriptService';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PineScriptEditor from '../../components/strategy/PineScriptEditor';
import TradingViewWidget from '../../components/trading/TradingViewWidget';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`labs-tabpanel-${index}`}
      style={{ overflow: 'visible', height: '100%', width: '100%' }}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const Labs = () => {
  const { state: { user } } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [editorMode, setEditorMode] = useState<'visual' | 'python' | 'pinescript'>('visual');
  const [pineScript, setPineScript] = useState<string>('');
  const [currentEditorScript, setCurrentEditorScript] = useState<string>('');
  const [showIntegratedChart, setShowIntegratedChart] = useState<boolean>(false);
  const [isPineScriptDialogOpen, setIsPineScriptDialogOpen] = useState(false);
  
  // Reference to the current getCode function from the editor
  const getEditorCodeRef = React.useRef<(() => string) | null>(null);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [filteredStrategies, setFilteredStrategies] = useState<Strategy[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  // Old builder dialog state removed
  const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'info' | 'warning' | 'error'}>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    if (user) {
      loadStrategies();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [selectedTags, strategies, searchQuery]);

  // Filter strategies based on search query and selected tags
  const applyFilters = () => {
    const filtered = strategies.filter(strategy => {
      // Filter by search query (case insensitive)
      const matchesSearch = searchQuery === '' || 
        strategy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (strategy.description && strategy.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Filter by selected tags
      const matchesTags = selectedTags.length === 0 || 
        (strategy.tags && selectedTags.every(tag => strategy.tags!.includes(tag)));
      
      return matchesSearch && matchesTags;
    });
    setFilteredStrategies(filtered);
  };

  const handleCreateStrategy = () => {
    // Redirect to Strategy Lab tab
    setActiveTab(0);
  };

  const handleEditStrategy = (strategy: Strategy) => {
    // Set selected strategy and redirect to Strategy Lab tab
    setSelectedStrategy(strategy);
    setActiveTab(0);
  };

  const handlePromoteToLive = async (strategy: Strategy) => {
    if (!user) return;

    try {
      const updatedStrategy: Partial<Strategy> = {
        ...strategy,
        status: StrategyStatus.LIVE,
        updatedAt: new Date().toISOString(),
      };
      await firestoreService.saveStrategy(updatedStrategy, user.id);
      setIsPromoteDialogOpen(false);
      loadStrategies();
    } catch (error) {
      console.error('Error promoting strategy:', error);
    }
  };

  const handleDeleteStrategy = async (strategyId: string) => {
    if (!user) return;

    try {
      await firestoreService.deleteStrategy(strategyId);
      setIsDeleteDialogOpen(false);
      setSelectedStrategy(null);
      loadStrategies();
    } catch (error) {
      console.error('Error deleting strategy:', error);
    }
  };

  const handleSaveStrategy = async (strategyData: Partial<Strategy>) => {
    if (!user) return;

    try {
      // Check if strategy name already exists
      const allStrategies = await firestoreService.getAllStrategies(user.id);
      const isDuplicateName = allStrategies.some(
        s => s.name === strategyData.name && s.id !== strategyData.id
      );

      if (isDuplicateName) {
        // Add a timestamp to make the name unique
        const timestamp = new Date().getTime().toString().slice(-4);
        const originalName = strategyData.name;
        strategyData.name = `${strategyData.name} (${timestamp})`;
        
        // Show notification
        setSnackbar({
          open: true,
          message: `Strategy name "${originalName}" was already taken. Renamed to "${strategyData.name}"`,
          severity: 'info'
        });
      }

      await firestoreService.saveStrategy(strategyData, user.id);
      loadStrategies();
      
      // Show success notification
      setSnackbar({
        open: true,
        message: 'Strategy saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving strategy:', error);
      setSnackbar({
        open: true,
        message: 'Error saving strategy',
        severity: 'error'
      });
    }
  };

  const handleTestStrategy = async (strategy: Strategy) => {
    // TODO: Implement strategy testing
    console.log('Testing strategy:', strategy);
  };

  const loadStrategies = async () => {
    if (!user) return;

    try {
      const allStrategies = await firestoreService.getAllStrategies(user.id);
      setStrategies(allStrategies.filter(s => s.status === StrategyStatus.DRAFT || s.status === StrategyStatus.TESTING));
      
      const allTags = allStrategies.flatMap(s => s.tags || []);
      const uniqueTags = Array.from(new Set(allTags));
      setAvailableTags(uniqueTags);
    } catch (error) {
      console.error('Error loading strategies:', error);
    }
  };

  const handleTagChange = (event: any, value: string[]) => {
    setSelectedTags(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Generate PineScript for TradingView
  const generatePineScript = (rules: any[]) => {
    const scriptName = selectedStrategy ? selectedStrategy.name : 'My Strategy';
    const scriptDesc = selectedStrategy ? selectedStrategy.description : 'Created with Strategiz';
    
    const script = pineScriptService.convertRulesToPineScript(rules, {
      version: PineScriptVersion.V5,
      strategyName: scriptName,
      description: scriptDesc,
      includeComments: true
    });
    
    setPineScript(script);
    setIsPineScriptDialogOpen(true);
  };

  // Generate PineScript from Python code
  const generatePineScriptFromPython = (pythonCode: string) => {
    const scriptName = selectedStrategy ? selectedStrategy.name : 'My Strategy';
    const scriptDesc = selectedStrategy ? selectedStrategy.description : 'Created with Strategiz';
    
    const script = pineScriptService.convertPythonToPineScript(pythonCode, {
      version: PineScriptVersion.V5,
      strategyName: scriptName,
      description: scriptDesc,
      includeComments: true
    });
    
    setPineScript(script);
    setIsPineScriptDialogOpen(true);
  };

  // Copy PineScript to clipboard
  const copyPineScript = () => {
    navigator.clipboard.writeText(pineScript);
    setSnackbar({
      open: true,
      message: 'PineScript copied to clipboard!',
      severity: 'success'
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Strategy Lab
          </Typography>
        </Box>

        <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)} aria-label="strategy tabs" sx={{ mb: 3 }}>
          <Tab label="Strategy Lab" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="My Strategies" id="tab-1" aria-controls="tabpanel-1" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Typography variant="h5" gutterBottom>Strategy Lab</Typography>
          <Typography variant="body1" paragraph>
            Experiment with different trading strategies and backtest them before deployment. Create a strategy here and then deploy it to your Strategies page when ready for live trading.
          </Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant={editorMode === 'visual' ? 'contained' : 'outlined'}
              onClick={() => setEditorMode('visual')}
              startIcon={<AddIcon />}
            >
              Visual Builder
            </Button>
            <Button
              variant={editorMode === 'python' ? 'contained' : 'outlined'}
              onClick={() => setEditorMode('python')}
              startIcon={<EditIcon />}
            >
              Python Editor
            </Button>
            <Button
              variant={editorMode === 'pinescript' ? 'contained' : 'outlined'}
              onClick={() => setEditorMode('pinescript')}
              startIcon={<EditIcon />}
            >
              PineScript Editor
            </Button>
            {editorMode !== 'pinescript' && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  if (editorMode === 'visual') {
                    // This would be populated with actual rules from the visual builder
                    generatePineScript([]);
                  } else if (editorMode === 'python') {
                    // This would be populated with actual Python code from the editor
                    generatePineScriptFromPython('');
                  }
                }}
                startIcon={<LaunchIcon />}
              >
                Generate TradingView Script
              </Button>
            )}
          </Box>
          
          <Box sx={{ minHeight: '500px', width: '100%', mt: 2 }}>
            {editorMode === 'visual' && (
              <VisualStrategyBuilder
                onSave={(rules) => {
                  // Save strategy to Firebase
                  const newStrategy: Partial<Strategy> = {
                    name: 'New Visual Strategy', // This should be obtained from the form
                    description: 'Created with Visual Builder',
                    type: StrategyType.CUSTOM,
                    mode: StrategyMode.EXPERIMENTAL,
                    status: StrategyStatus.DRAFT,
                    metadata: {
                      conditions: rules
                    },
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  };
                  handleSaveStrategy(newStrategy);
                }}
                onTest={(rules) => {
                  // Run backtest
                  setSnackbar({
                    open: true,
                    message: 'Backtest started...',
                    severity: 'info'
                  });
                  // Also generate PineScript for TradingView testing
                  generatePineScript(rules);
                }}
              />
            )}
            
            {editorMode === 'python' && (
              <Box sx={{ height: '500px', border: '1px solid #444', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: 2 }}>
                <Box sx={{ p: 2, bgcolor: '#1a1a1a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Python Strategy Editor</Typography>
                  <Box>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<PlayArrowIcon />}
                      sx={{ mr: 1 }}
                      onClick={() => {
                        // Trigger run with current code
                        const editor = document.querySelector('.monaco-editor');
                        if (editor) {
                          const model = (editor as any)?.getModel?.();
                          if (model) {
                            const code = model.getValue();
                            generatePineScriptFromPython(code);
                          }
                        }
                      }}
                    >
                      Run Backtest
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      startIcon={<SaveIcon />}
                    >
                      Save Strategy
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <PyStrategyEditor
                    strategyId="new"
                    onSave={(id, newCode) => {
                      // Save Python code to Firebase
                      const newStrategy: Partial<Strategy> = {
                        name: 'New Python Strategy', // This should be obtained from the form
                        description: 'Created with Python Editor',
                        type: StrategyType.CUSTOM,
                        mode: StrategyMode.EXPERIMENTAL,
                        status: StrategyStatus.DRAFT,
                        code: newCode,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                      };
                      handleSaveStrategy(newStrategy);
                    }}
                    onRun={(code) => {
                      // Run backtest with Python code
                      setSnackbar({
                        open: true,
                        message: 'Python backtest started...',
                        severity: 'info'
                      });
                      // Generate PineScript from Python code
                      generatePineScriptFromPython(code);
                    }}
                  />
                </Box>
              </Box>
            )}
            
            {editorMode === 'pinescript' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Editor Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">PineScript Editor</Typography>
                  <Box>
                    <Button 
                      variant={showIntegratedChart ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => setShowIntegratedChart(!showIntegratedChart)}
                      sx={{ mr: 1 }}
                    >
                      {showIntegratedChart ? 'Hide Chart' : 'Show Live Chart'}
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<LaunchIcon />}
                      sx={{ mr: 1 }}
                      onClick={() => {
                        // Get editor content
                        const editor = document.querySelector('.monaco-editor');
                        if (editor) {
                          const model = (editor as any)?.getModel?.();
                          if (model) {
                            const code = model.getValue();
                            setPineScript(code);
                            setIsPineScriptDialogOpen(true);
                          }
                        }
                      }}
                    >
                      Open in TradingView
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => {
                        // Get editor content
                        const editor = document.querySelector('.monaco-editor');
                        if (editor) {
                          const model = (editor as any)?.getModel?.();
                          if (model) {
                            const code = model.getValue();
                            // Save PineScript to Firebase
                            const newStrategy: Partial<Strategy> = {
                              name: 'New PineScript Strategy', // This should be obtained from the form
                              description: 'Created with PineScript Editor',
                              type: StrategyType.CUSTOM,
                              mode: StrategyMode.EXPERIMENTAL,
                              status: StrategyStatus.DRAFT,
                              code: code, // Use the code field to store PineScript
                              metadata: {
                                scriptType: 'pinescript'
                              },
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString(),
                            };
                            handleSaveStrategy(newStrategy);
                          }
                        }
                      }}
                    >
                      Save Strategy
                    </Button>
                  </Box>
                </Box>
                
                {/* Editor and Chart Layout - Vertical Stack */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: showIntegratedChart ? '900px' : '500px' }}>
                  
                  {/* TradingView Chart Panel - Top Position when shown */}
                  {showIntegratedChart && (
                    <Box sx={{ 
                      flex: 3, 
                      border: '1px solid #444', 
                      borderRadius: 1, 
                      overflow: 'hidden',
                      boxShadow: 2,
                      minHeight: '450px'
                    }}>
                      <Box sx={{ p: 2, bgcolor: '#1a1a1a', color: 'white', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <Typography variant="subtitle1">TradingView Chart</Typography>
                          <Box>
                            <Button 
                              variant="outlined" 
                              color="primary"
                              size="small"
                              startIcon={<LaunchIcon />}
                              onClick={() => {
                                window.open('https://www.tradingview.com/chart/', '_blank');
                              }}
                            >
                              Open in TradingView
                            </Button>
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', maxWidth: 600 }}>
                          Note: Buy/sell signals require full TradingView access. Click "Open in TradingView" to see all signals.
                        </Typography>
                      </Box>
                      <Box sx={{ height: 'calc(100% - 60px)' }}>
                        <TradingViewWidget 
                          key={currentEditorScript ? `pine-${Date.now()}` : 'default-chart'}
                          symbol="BINANCE:BTCUSDT"
                          theme="dark"
                          interval="60"
                          pineScript={currentEditorScript}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Editor Panel - Below Chart */}
                  <Box sx={{ 
                    flex: showIntegratedChart ? 2 : 1, 
                    border: '1px solid #444', 
                    borderRadius: 1, 
                    overflow: 'hidden', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    boxShadow: 2,
                    minHeight: '300px'
                  }}>
                    <Box sx={{ p: 2, bgcolor: '#1a1a1a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1">Script Editor</Typography>
                      <Button 
                        variant="contained" 
                        color="primary"
                        size="small"
                        onClick={() => {
                          // Get code from our reference to the editor
                          if (getEditorCodeRef.current) {
                            const code = getEditorCodeRef.current();
                            // Force re-render of chart with new script
                            setCurrentEditorScript(code + ' // ' + new Date().getTime());
                            // Show a notification
                            setSnackbar({
                              open: true,
                              message: 'Script applied to chart',
                              severity: 'success'
                            });
                          } else {
                            // Show error if we can't access the editor
                            setSnackbar({
                              open: true,
                              message: 'Error: Could not access editor content',
                              severity: 'error'
                            });
                          }
                        }}
                      >
                        Apply to Chart
                      </Button>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <PineScriptEditor
                        height={showIntegratedChart ? '250px' : '500px'}
                        onEditorReady={(getCode) => {
                          // Store reference to the getCode function
                          getEditorCodeRef.current = getCode;
                        }}
                        onSave={(code) => {
                          // Save PineScript to Firebase
                          const newStrategy: Partial<Strategy> = {
                            name: 'New PineScript Strategy',
                            description: 'Created with PineScript Editor',
                            type: StrategyType.CUSTOM,
                            mode: StrategyMode.EXPERIMENTAL,
                            status: StrategyStatus.DRAFT,
                            code: code,
                            metadata: {
                              scriptType: 'pinescript'
                            },
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                          };
                          handleSaveStrategy(newStrategy);
                        }}
                        onTest={(code) => {
                          // No conversion needed - directly use PineScript
                          setPineScript(code);
                          // Also update the chart if showing
                          setCurrentEditorScript(code);
                          setIsPineScriptDialogOpen(true);
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
            <TextField
              id="search"
              label="Search by name or description"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              multiple
              id="tags-filled"
              options={availableTags}
              defaultValue={[]}
              freeSolo
              onChange={handleTagChange}
              sx={{ minWidth: '50%', flexGrow: 1 }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const { key, ...tagPropsWithoutKey } = tagProps;
                  return (
                    <Chip key={index} label={option} {...tagPropsWithoutKey} />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Filter by tags"
                  placeholder="Select tags"
                />
              )}
            />
          </Box>
          {filteredStrategies.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              my: 4, 
              p: 4, 
              border: '1px dashed', 
              borderColor: 'divider', 
              borderRadius: 2 
            }}>
              <Typography variant="h6" gutterBottom>No Strategies in Your Lab</Typography>
              <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 2 }}>
                You don't have any strategies in your lab yet. Create your first strategy to get started.
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleCreateStrategy}
              >
                Create Strategy
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredStrategies.map((strategy) => (
                <Grid item xs={12} md={6} key={strategy.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#1976d2' }}>{strategy.name}</Typography>
                        <Box>
                          <Tooltip title="Edit in Strategy Lab">
                            <IconButton 
                              onClick={() => handleEditStrategy(strategy)}
                              color="primary"
                              size="medium"
                              aria-label="Edit strategy in lab"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deploy to Live Strategies">
                            <IconButton 
                              onClick={() => {
                                setSelectedStrategy(strategy);
                                setIsPromoteDialogOpen(true);
                              }}
                              color="primary"
                              size="medium"
                              aria-label="Deploy to live strategies"
                            >
                              <LaunchIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Strategy">
                            <IconButton 
                              onClick={() => {
                                setSelectedStrategy(strategy);
                                setIsDeleteDialogOpen(true);
                              }}
                              color="error"
                              size="medium"
                              aria-label="Delete strategy"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography color="textSecondary" variant="body2" sx={{ mt: 1, mb: 2 }}>
                        {strategy.description || 'No description provided.'}
                      </Typography>
                      <Typography variant="body2">
                        Mode: {strategy.mode} | Type: {strategy.type}
                      </Typography>
                      {strategy.tags && (
                        <Box sx={{ mt: 1 }}>
                          {strategy.tags.map((tag, index) => (
                            <Chip key={index} label={tag} sx={{ mr: 1 }} />
                          ))}
                        </Box>
                      )}
                      {strategy.backtestResults && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2">Latest Backtest Results:</Typography>
                          <Typography variant="body2">
                            Returns: {strategy.backtestResults.totalReturns}% | 
                            Sharpe: {strategy.backtestResults.sharpeRatio.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Old Strategy Builder Dialog removed */}

        {/* PineScript Dialog */}
        <Dialog
          open={isPineScriptDialogOpen}
          onClose={() => setIsPineScriptDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">TradingView PineScript</Typography>
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={copyPineScript}
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  sx={{ mr: 1 }}
                >
                  Copy to Clipboard
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    // Open TradingView with the script content
                    const encodedScript = encodeURIComponent(pineScript);
                    const tradingViewUrl = `https://www.tradingview.com/chart/?script=${encodedScript}`;
                    window.open(tradingViewUrl, '_blank');
                  }}
                  size="small"
                  startIcon={<LaunchIcon />}
                >
                  Apply to Chart
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Copy this script to TradingView Pine Editor to visualize and backtest your strategy, or click "Apply to Chart" to open it directly in TradingView.
            </Typography>
            <Box
              sx={{
                bgcolor: '#1a1a1a',
                color: '#e0e0e0',
                p: 2,
                borderRadius: 1,
                maxHeight: '400px',
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap'
              }}
            >
              {pineScript}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsPineScriptDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                window.open('https://www.tradingview.com/pine-script-editor/', '_blank');
              }}
            >
              Open TradingView Editor
            </Button>
          </DialogActions>
        </Dialog>

        {/* Promote Dialog */}
        <Dialog
          open={isPromoteDialogOpen}
          onClose={() => setIsPromoteDialogOpen(false)}
        >
          <DialogTitle>Deploy to Live Strategies</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to promote this strategy to live status? 
              Once promoted, it will be moved from the Strategy Lab to the Live Strategies page.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsPromoteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => selectedStrategy && handlePromoteToLive(selectedStrategy)} 
              color="primary" 
              variant="contained"
            >
              Deploy to Live
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Strategy Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Strategy?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this strategy? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => selectedStrategy?.id && handleDeleteStrategy(selectedStrategy.id)} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({...snackbar, open: false})}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({...snackbar, open: false})} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};
